import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export const SUPPORTED_LANGUAGES = ['en', 'es', 'ar'] as const;
export const DEFAULT_LANGUAGE = 'en';
export const PREFERRED_LANG_STORAGE_KEY = 'preferredLang';

export function resolvePreferredLanguage(): string {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(PREFERRED_LANG_STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked site data).
  }
  return saved && (SUPPORTED_LANGUAGES as readonly string[]).includes(saved) ? saved : DEFAULT_LANGUAGE;
}

/**
 * Runs once at startup, before the first component renders. Loads the user's
 * language and the English fallback so that `translate.instant()` calls made in
 * constructors / ngOnInit never return raw keys on a hard refresh or deep link.
 */
export function translationInitFactory(translate: TranslateService): () => Promise<void> {
  return async () => {
    const lang = resolvePreferredLanguage();
    document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
    try {
      translate.setDefaultLang(DEFAULT_LANGUAGE);
      await firstValueFrom(translate.use(lang));
      if (lang !== DEFAULT_LANGUAGE && !translate.translations[DEFAULT_LANGUAGE]) {
        await firstValueFrom(translate.getTranslation(DEFAULT_LANGUAGE));
      }
    } catch (e) {
      // Never block the app from booting because a dictionary failed to load.
      console.error('Failed to load translations', e);
    }
  };
}
